import secureApiService from './secureApiService'
import {
  ProgrammeCohortJSON,
  ProgrammeCohortModel,
  ProgrammePaymentAccountJSON,
} from '@/models/programme.model'

export interface ProgrammeCohortMutationPayload {
  title: string
  startDate?: string | null
  endDate?: string | null
  examDate?: string | null
  firstYrFirstSemesterStartDate?: string | null
  firstYrFirstSemesterEndDate?: string | null
  firstYrSecondSemesterStartDate?: string | null
  firstYrSecondSemesterEndDate?: string | null
  secondYrFirstSemesterStartDate?: string | null
  secondYrFirstSemesterEndDate?: string | null
  secondYrSecondSemesterStartDate?: string | null
  secondYrSecondSemesterEndDate?: string | null
  firstYrFirstSemesterExamDate?: string | null
  firstYrSecondSemesterExamDate?: string | null
  secondYrFirstSemesterExamDate?: string | null
  secondYrSecondSemesterExamDate?: string | null
  capacity?: number | null
  fee?: number | null
  currency?: string | null
  isActive?: boolean | null
  payment_accounts?: ProgrammePaymentAccountJSON[] | null
}

interface ProgrammeCohortCollectionResponseJSON {
  success: boolean
  message?: string
  data?: ProgrammeCohortJSON[] | ProgrammeCohortJSON | null
}

interface ProgrammeCohortSingleResponseJSON {
  success: boolean
  message?: string
  data?: ProgrammeCohortJSON | null
}

class ProgrammeCohortService {
  private getBasePath(programmeId: string) {
    return `/programmes/${programmeId}/cohorts`
  }

  async getCohorts(programmeId: string): Promise<ProgrammeCohortModel[]> {
    const response = await secureApiService.get<ProgrammeCohortCollectionResponseJSON>(
      this.getBasePath(programmeId),
      { requireAuth: true },
    )

    const payload = response.data

    if (!payload?.success) {
      throw new Error(payload?.message || 'Unable to fetch programme cohorts')
    }

    const rawData = Array.isArray(payload.data)
      ? payload.data
      : payload.data
        ? [payload.data]
        : []

    return rawData
      .filter((item): item is ProgrammeCohortJSON => Boolean(item))
      .map((item) => ProgrammeCohortModel.fromJSON(item))
  }

  async getCohort(programmeId: string, cohortId: string): Promise<ProgrammeCohortModel> {
    const response = await secureApiService.get<ProgrammeCohortSingleResponseJSON>(
      `${this.getBasePath(programmeId)}/${cohortId}`,
      { requireAuth: true },
    )

    const payload = response.data

    if (!payload?.success || !payload.data) {
      throw new Error(payload?.message || 'Unable to fetch programme cohort')
    }

    return ProgrammeCohortModel.fromJSON(payload.data)
  }

  async createCohort(
    programmeId: string,
    payload: ProgrammeCohortMutationPayload,
  ): Promise<ProgrammeCohortModel> {
    const response = await secureApiService.post<ProgrammeCohortSingleResponseJSON>(
      this.getBasePath(programmeId),
      this.buildRequestPayload(payload),
    )

    const body = response.data

    if (!body?.success || !body.data) {
      throw new Error(body?.message || 'Unable to create programme cohort')
    }

    return ProgrammeCohortModel.fromJSON(body.data)
  }

  async updateCohort(
    programmeId: string,
    cohortId: string,
    payload: ProgrammeCohortMutationPayload,
  ): Promise<ProgrammeCohortModel> {
    const response = await secureApiService.put<ProgrammeCohortSingleResponseJSON>(
      `${this.getBasePath(programmeId)}/${cohortId}`,
      this.buildRequestPayload(payload),
    )

    const body = response.data

    if (!body?.success || !body.data) {
      throw new Error(body?.message || 'Unable to update programme cohort')
    }

    return ProgrammeCohortModel.fromJSON(body.data)
  }

  async deleteCohort(programmeId: string, cohortId: string): Promise<void> {
    const response = await secureApiService.delete<ProgrammeCohortSingleResponseJSON>(
      `${this.getBasePath(programmeId)}/${cohortId}`,
      { requireAuth: true },
    )

    const body = response.data

    if (!body?.success) {
      throw new Error(body?.message || 'Unable to delete programme cohort')
    }
  }

  private buildRequestPayload(payload: ProgrammeCohortMutationPayload) {
    const body: Record<string, unknown> = {
      title: payload.title,
      isActive: payload.isActive ?? true,
    }

    if (payload.startDate) {
      body.startDate = payload.startDate
    }

    if (payload.endDate) {
      body.endDate = payload.endDate
    }

    if (payload.firstYrFirstSemesterStartDate) {
      body.firstYrFirstSemesterStartDate = payload.firstYrFirstSemesterStartDate
    }
    if (payload.firstYrFirstSemesterEndDate) {
      body.firstYrFirstSemesterEndDate = payload.firstYrFirstSemesterEndDate
    }
    if (payload.firstYrSecondSemesterStartDate) {
      body.firstYrSecondSemesterStartDate = payload.firstYrSecondSemesterStartDate
    }
    if (payload.firstYrSecondSemesterEndDate) {
      body.firstYrSecondSemesterEndDate = payload.firstYrSecondSemesterEndDate
    }
    if (payload.secondYrFirstSemesterStartDate) {
      body.secondYrFirstSemesterStartDate = payload.secondYrFirstSemesterStartDate
    }
    if (payload.secondYrFirstSemesterEndDate) {
      body.secondYrFirstSemesterEndDate = payload.secondYrFirstSemesterEndDate
    }
    if (payload.secondYrSecondSemesterStartDate) {
      body.secondYrSecondSemesterStartDate = payload.secondYrSecondSemesterStartDate
    }
    if (payload.secondYrSecondSemesterEndDate) {
      body.secondYrSecondSemesterEndDate = payload.secondYrSecondSemesterEndDate
    }
    if (payload.examDate) {
      body.examDate = payload.examDate
    }
    if (payload.firstYrFirstSemesterExamDate) {
      body.firstYrFirstSemesterExamDate = payload.firstYrFirstSemesterExamDate
    }
    if (payload.firstYrSecondSemesterExamDate) {
      body.firstYrSecondSemesterExamDate = payload.firstYrSecondSemesterExamDate
    }
    if (payload.secondYrFirstSemesterExamDate) {
      body.secondYrFirstSemesterExamDate = payload.secondYrFirstSemesterExamDate
    }
    if (payload.secondYrSecondSemesterExamDate) {
      body.secondYrSecondSemesterExamDate = payload.secondYrSecondSemesterExamDate
    }

    if (payload.capacity !== null && payload.capacity !== undefined) {
      body.capacity = payload.capacity
    }

    if (payload.fee !== null && payload.fee !== undefined) {
      body.fee = payload.fee
    }

    if (payload.currency) {
      body.currency = payload.currency
    }

    if (payload.payment_accounts) {
      body.payment_accounts = payload.payment_accounts
    }

    return body
  }
}

export const programmeCohortService = new ProgrammeCohortService()
